/**
 * Tenancy Account Slide-over Alpine.js Component
 *
 * Displays tenancy accounting details: balance, monthly rent,
 * deposit info, rent history, and transaction ledger.
 */

import { legacyImportMixin } from './legacy_import_mixin'
import { transactionSearchMixin } from './transaction_search_mixin'
import { tenantStatementModalMixin } from './tenant_statement_modal_mixin'

export function tenancyAccountSlideover() {
  return {
    ...legacyImportMixin('tenancy'),
    ...transactionSearchMixin('tenancy'),
    ...tenantStatementModalMixin(),

    // State
    open: false,
    aiPanelOpen: false,
    loading: false,
    loadingMore: false,
    error: null,
    zIndex: 100010,

    // Data
    tenancyId: null,
    tenancy: null,
    stats: null,
    rentSchedule: [],
    rentHistory: [],
    transactions: [],

    // Pagination (transactions)
    page: 1,
    hasMore: false,

    // Kebab menu
    menuOpen: false,
    showBackToTop: false,

    init() {
      window.addEventListener('refresh-tenancy-account', (e) => {
        if (this.open && String(this.tenancyId) === String(e.detail?.id)) {
          this.refreshData()
        }
      })
      window.addEventListener('scroll-to-transaction', (e) => {
        if (this.open && e.detail?.id) this.scrollToTransaction(e.detail.id)
      })
    },

    // Open — receives { id } from event detail
    openSlideover(detail) {
      const id = detail?.id
      if (!id) return

      const store = Alpine.store?.('slideovers')

      // Bring to front if already open for this tenancy
      if (store?.isOpen?.('tenancy-account', id)) {
        store.bringToFront('tenancy-account', id)
        return
      }

      // Get next z-index
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.tenancyId = id
      this.open = true
      this.menuOpen = false
      this.clearSearch()
      this.transactions = []
      this.rentSchedule = []
      this.rentHistory = []
      this.page = 1
      this.hasMore = false

      // Register
      if (store?.register) {
        store.register('tenancy-account', id, this)
      }

      this.fetchData(true)
    },

    // Close the slideover
    close() {
      const store = Alpine.store?.('slideovers')
      if (store && this.tenancyId) {
        store.unregister('tenancy-account', this.tenancyId)
      }

      this.menuOpen = false
      this.$nextTick(() => {
        this.open = false
        window.dispatchEvent(new CustomEvent('account-slideover-closed'))
      })
    },

    // Fetch data from controller
    async fetchData(initial = false) {
      if (initial) {
        this.loading = true
        this.page = 1
        this.transactions = []
      } else {
        this.loadingMore = true
      }
      this.error = null

      try {
        const params = new URLSearchParams()
        params.set('page', this.page)

        const response = await fetch(`/accounting/accounts/tenancies/${this.tenancyId}.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        this.tenancy = data.tenancy
        this.stats = data.stats
        this.rentSchedule = data.rent_schedule || []
        this.rentHistory = data.rent_history || []

        if (initial) {
          this.transactions = data.transactions?.lines || []
        } else {
          this.transactions = [...this.transactions, ...(data.transactions?.lines || [])]
        }

        this.hasMore = data.transactions?.pagination?.has_more || false
      } catch (error) {
        console.error('Failed to fetch tenancy account:', error)
        this.error = 'Failed to load tenancy account details. Please try again.'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    // Silent refresh — keeps existing content visible while fetching updated data
    async refreshData() {
      this.error = null

      try {
        const params = new URLSearchParams()
        params.set('page', 1)

        const response = await fetch(`/accounting/accounts/tenancies/${this.tenancyId}.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

        const data = await response.json()
        this.tenancy = data.tenancy
        this.stats = data.stats
        this.rentSchedule = data.rent_schedule || []
        this.rentHistory = data.rent_history || []
        this.transactions = data.transactions?.lines || []
        this.page = 1
        this.hasMore = data.transactions?.pagination?.has_more || false
      } catch (error) {
        console.error('Failed to refresh tenancy account:', error)
      }
    },

    // Load next page (transactions only)
    loadMore() {
      if (this.loadingMore || !this.hasMore) return
      this.page++
      this.fetchData(false)
    },

    // Infinite scroll handler
    handleScroll(event) {
      this.showBackToTop = window.shouldShowBackToTop(event)

      const el = event.target
      const scrollPosition = el.scrollTop + el.clientHeight
      const threshold = el.scrollHeight - 200

      if (scrollPosition >= threshold && this.hasMore && !this.loadingMore) {
        this.loadMore()
      }
    },

    scrollToTop() {
      window.scrollToTopHelper(this.$refs.scrollContainer)
    },

    // Dynamic text size for stat cards
    get statValueClass() {
      if (!this.stats) return 'text-2xl'
      const lengths = [
        this.formatPence(this.stats.balance_pence).length,
        this.formatPence(this.stats.monthly_rent_pence).length,
        this.formatPence(this.stats.deposit_pence).length
      ]
      const max = Math.max(...lengths)
      if (max > 9) return 'text-base'
      if (max > 7) return 'text-lg'
      return 'text-2xl'
    },

    // Stat card subtitle text
    get balanceSubtitle() {
      if (!this.stats) return ''
      const status = this.stats.balance_status
      if (status === 'clear') return 'Clear'
      if (status === 'overdue') return 'Overdue'
      return 'In credit'
    },

    get rentDueSubtitle() {
      if (!this.stats) return ''
      const day = this.stats.rent_due_day || 1
      const ordinal = this.ordinalSuffix(day)
      return `Due ${day}${ordinal} of each month`
    },

    get depositSubtitle() {
      if (!this.stats) return ''
      return this.stats.deposit_subtitle || 'N/A'
    },

    // Ordinal suffix helper
    ordinalSuffix(day) {
      if (day >= 11 && day <= 13) return 'th'
      switch (day % 10) {
        case 1: return 'st'
        case 2: return 'nd'
        case 3: return 'rd'
        default: return 'th'
      }
    },

    // Balance border-t color class
    get balanceBorderClass() {
      if (!this.stats) return 'border-gray-800 dark:border-gray-200'
      if (this.stats.balance_status === 'overdue') return 'border-red-500'
      return 'border-gray-800 dark:border-gray-200'
    },

    // Balance value color class
    get balanceValueClass() {
      if (!this.stats) return 'text-gray-900 dark:text-white'
      if (this.stats.balance_status === 'overdue') return 'text-red-600 dark:text-red-400'
      return 'text-gray-900 dark:text-white'
    },

    // Balance label color class
    get balanceLabelClass() {
      if (!this.stats) return 'text-gray-600'
      if (this.stats.balance_status === 'overdue') return 'text-red-600 dark:text-red-400'
      return 'text-gray-600'
    },

    // Format helpers
    formatPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const isNegative = pence < 0
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return (isNegative ? '-' : '') + '\u00A3' + formatted
    },

    formatSignedPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const prefix = pence >= 0 ? '+' : '-'
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return `${prefix}\u00A3${formatted}`
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr + 'T00:00:00')
      const day = date.getDate()
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`
    },

    txnBarClass(txn) {
      if (txn.payment_status === 'unreconciled') return 'bg-amber-400'
      if (txn.charge_status === 'unsettled' || txn.charge_status === 'outstanding' || txn.charge_status === 'partial') return 'bg-amber-400'
      return 'bg-blue-400'
    },

    amountColorClass(pence) {
      if (pence > 0) return 'text-emerald-600 dark:text-emerald-400'
      if (pence < 0) return 'text-red-600 dark:text-red-400'
      return 'text-gray-500 dark:text-gray-400'
    }
  }
}
